import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Voice messages (FRD-CORE-067; CLR-62; SDD §29.7): the browser's own recorder, saved as an
 * attachment of the message like any other file. Nothing is uploaded until the message is sent.
 */

/** A format the browser can record and the chat attachment type accepts. */
export interface RecorderFormat {
  /** What MediaRecorder is asked for. */
  readonly mimeType: string;
  /** The type the upload declares and the API verifies (SDD §12.5). */
  readonly contentType: string;
  readonly extension: string;
}

/** Chrome, Edge and Firefox record WebM or Ogg with Opus; Safari records MP4 (AAC). */
const FORMATS: readonly RecorderFormat[] = [
  { mimeType: 'audio/webm;codecs=opus', contentType: 'audio/webm', extension: 'webm' },
  { mimeType: 'audio/webm', contentType: 'audio/webm', extension: 'webm' },
  { mimeType: 'audio/ogg;codecs=opus', contentType: 'audio/ogg', extension: 'ogg' },
  { mimeType: 'audio/mp4', contentType: 'audio/mp4', extension: 'm4a' },
];

/**
 * Speech needs far less than the recorder's default bit rate: at 32 kbit/s a 5 MB document limit
 * holds about twenty minutes, so the ten-minute cap below always fits.
 */
const BITS_PER_SECOND = 32_000;
/** The longest voice message, in seconds; recording stops by itself there. */
export const VOICE_MAX_SECONDS = 600;

/** The first format this browser records that `allowed` accepts; null when there is none. */
export function recorderFormat(allowed: readonly string[]): RecorderFormat | null {
  if (
    typeof MediaRecorder === 'undefined' ||
    typeof navigator === 'undefined' ||
    // Absent outside a secure context (HTTPS or localhost).
    !('mediaDevices' in navigator)
  ) {
    return null;
  }
  return (
    FORMATS.find(
      (format) =>
        allowed.includes(format.contentType) && MediaRecorder.isTypeSupported(format.mimeType),
    ) ?? null
  );
}

/** 75 → "1:15". */
export function durationLabel(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${String(Math.floor(whole / 60))}:${String(whole % 60).padStart(2, '0')}`;
}

/** A finished recording, ready to attach. */
export interface VoiceRecording {
  readonly file: File;
  readonly seconds: number;
}

function fileName(format: RecorderFormat, at: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${String(at.getFullYear())}${pad(at.getMonth() + 1)}${pad(at.getDate())}-${pad(at.getHours())}${pad(at.getMinutes())}${pad(at.getSeconds())}`;
  return `voice-message-${stamp}.${format.extension}`;
}

/**
 * Records from the microphone. `start` asks the browser for the microphone (the site allows it
 * for its own origin only, SDD §12.5); `stop` hands the recording to `onRecorded`; `discard` drops
 * it. The microphone is released as soon as recording ends, and when the component goes away.
 */
export function useVoiceRecorder(
  format: RecorderFormat | null,
  onRecorded: (recording: VoiceRecording) => void,
) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const keep = useRef(true);
  const startedAt = useRef(0);
  const delivered = useRef(onRecorded);
  useEffect(() => {
    delivered.current = onRecorded;
  });

  const release = useCallback(() => {
    stream.current?.getTracks().forEach((track) => {
      track.stop();
    });
    stream.current = null;
    recorder.current = null;
    setRecording(false);
  }, []);

  const finish = useCallback((save: boolean) => {
    keep.current = save;
    if (recorder.current?.state === 'recording') recorder.current.stop();
  }, []);

  useEffect(() => {
    if (!recording) return;
    const timer = setInterval(() => {
      const elapsed = (Date.now() - startedAt.current) / 1000;
      setSeconds(elapsed);
      if (elapsed >= VOICE_MAX_SECONDS) finish(true);
    }, 250);
    return () => {
      clearInterval(timer);
    };
  }, [recording, finish]);

  // Leaving the conversation mid-recording discards it and frees the microphone.
  useEffect(
    () => () => {
      keep.current = false;
      if (recorder.current?.state === 'recording') recorder.current.stop();
      stream.current?.getTracks().forEach((track) => {
        track.stop();
      });
    },
    [],
  );

  const start = useCallback(async () => {
    if (format === null || recorder.current !== null) return;
    setError(null);
    let media: MediaStream;
    try {
      media = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (cause) {
      setError(
        cause instanceof DOMException && cause.name === 'NotAllowedError'
          ? 'The browser was not allowed to use the microphone. Allow it for this site to record.'
          : 'No microphone could be used. Check that one is connected.',
      );
      return;
    }
    const chunks: Blob[] = [];
    const next = new MediaRecorder(media, {
      mimeType: format.mimeType,
      audioBitsPerSecond: BITS_PER_SECOND,
    });
    next.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    next.onstop = () => {
      const elapsed = (Date.now() - startedAt.current) / 1000;
      release();
      if (!keep.current || chunks.length === 0) return;
      const file = new File(chunks, fileName(format, new Date()), { type: format.contentType });
      delivered.current({ file, seconds: elapsed });
    };
    stream.current = media;
    recorder.current = next;
    keep.current = true;
    startedAt.current = Date.now();
    setSeconds(0);
    next.start(1000);
    setRecording(true);
  }, [format, release]);

  return {
    recording,
    seconds,
    error,
    start,
    stop: () => {
      finish(true);
    },
    discard: () => {
      finish(false);
    },
  };
}
